import {
  SCHEDULE_FREQUENCY_LABELS,
  SCHEDULE_TIME_OPTIONS,
} from "@/components/news-alerts/newsAlertsHelpers";
import { Label } from "@/components/ui/field";
import Autocomplete from "@/components/ui/molecules/autocomplete/Autocomplete";
import { JollyTextField } from "@/components/ui/textfield";
import { AvailableTimezonesQuery } from "@/data-access/news-alerts/queries/timezone-queries";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import { updateCreateEditNewsAlert } from "@/store/news-alerts/news-alerts.actions";
import useNewsAlertsStore from "@/store/news-alerts/news-alerts.slice";
import { useMemo } from "react";
import { useQuery } from "urql";

const CreateEditNewsAlertModalSchedule = () => {
  const createEditNewsAlert = useNewsAlertsStore(
    (state) => state.createEditNewsAlert,
  );
  const { scheduleFrequency, scheduleTime, timezone } = createEditNewsAlert;

  const [{ data, fetching: isFetchingAvailableTimezones }] = useQuery({
    query: AvailableTimezonesQuery,
    requestPolicy: "cache-first",
  });

  const availableTimezones = useMemo(() => {
    const timezones = data?.availableTimezones ?? [];
    if (timezones.length === 0) return [];

    // Get user's local IANA timezone (e.g., "America/New_York")
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert to backend format: uppercase with underscores (e.g., "AMERICA_NEW_YORK")
    const localTimezoneFormatted = localTimezone
      .toUpperCase()
      .replace(/\//g, "_");

    // Find matching timezone - value is in format like "AMERICA_NEW_YORK"
    const index = timezones.findIndex(
      (tz) => tz?.value === localTimezoneFormatted,
    );

    if (index === -1) return timezones;

    const found = timezones[index];
    return [found, ...timezones.slice(0, index), ...timezones.slice(index + 1)];
  }, [data?.availableTimezones]);

  return (
    <div className="flex flex-col gap-1">
      <Label aria-label="schedule-label" className="text-sm text-gray-700">
        Schedule
      </Label>
      <div className="flex flex-row flex-wrap gap-2 w-full">
        <div className="flex-1 min-w-72 max-w-1/3">
          <JollyTextField
            label="Send"
            labelVariant="secondary"
            size="sm"
            value={SCHEDULE_FREQUENCY_LABELS[scheduleFrequency]}
            isDisabled={true}
            className="justify-between"
            clearable={false}
          />
        </div>
        <Autocomplete
          clearable={true}
          label="At"
          labelVariant="secondary"
          labelSize="sm"
          options={SCHEDULE_TIME_OPTIONS}
          onValueChange={(value) =>
            updateCreateEditNewsAlert({
              ...createEditNewsAlert,
              scheduleTime: value as string,
            })
          }
          value={scheduleTime}
          disabled={false}
          className="justify-between flex-1 min-w-72 max-w-1/3"
          defaultToNone={false}
        />
        <Autocomplete
          clearable={true}
          label="Timezone"
          labelVariant="secondary"
          labelSize="sm"
          options={availableTimezones}
          onValueChange={(value) =>
            updateCreateEditNewsAlert({
              ...createEditNewsAlert,
              timezone: value as TimezoneChoices,
            })
          }
          value={timezone}
          disabled={false}
          className="justify-between flex-1 min-w-72 max-w-1/3"
          isPending={isFetchingAvailableTimezones}
          defaultToNone={false}
        />
      </div>
    </div>
  );
};

export default CreateEditNewsAlertModalSchedule;
